/** Dossier views reuse existing server projections and commands; view selection lives in the URL. */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";

import { BlocRepliable } from "../../components/BlocRepliable";
import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { PROJECT_VIEWS } from "../../lib/preparationNavigation";
import { useBroker } from "../../lib/queries/brokers";
import { useCompany } from "../../lib/queries/companies";
import { useProject } from "../../lib/queries/projects";
import { useProjectAnchor } from "../../lib/useProjectAnchor";
import { PreparationCabinetGate } from "../quote-requests/_parts/PreparationCabinetGate";
import { PrepareSendWorkspace } from "../quote-requests/PrepareSendPage";

import { CensusSection } from "./_parts/CensusSection";
import { DocumentLinksSection } from "./_parts/DocumentLinksSection";
import { DossierReadinessSection } from "./_parts/DossierReadinessSection";
import { EtatOperationnelBadge } from "./_parts/EtatOperationnel";
import { InsurerLoopSection } from "./_parts/InsurerLoopSection";
import { MissingInformationSection } from "./_parts/MissingInformationSection";
import { PreparationReturn } from "./_parts/PreparationReturn";
import { ProchaineAction } from "./_parts/ProchaineAction";
import { ProjectDeadlinesSection } from "./_parts/ProjectDeadlinesSection";
import { ProjectFilesSection } from "./_parts/ProjectFilesSection";
import { ProjectLifecycleSection } from "./_parts/ProjectLifecycleSection";
import { ProjectQuoteRequestsSection } from "./_parts/ProjectQuoteRequestsSection";
import { ProjectStatusBadge } from "./_parts/ProjectStatusBadge";
import { ProjectSubmissionsList } from "./_parts/ProjectSubmissionsList";
import { RequestContext } from "./_parts/RequestContext";

export function ProjectDetailPage() {
  return (
    <PreparationCabinetGate>
      <ScopedProjectDetailPage />
    </PreparationCabinetGate>
  );
}

function ScopedProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const { hash } = useLocation();
  const [searchParams] = useSearchParams();
  const requestedView = searchParams.get("view");
  const view =
    hash === "#quote-requests" || hash === "#census"
      ? "preparation"
      : (PROJECT_VIEWS.find((item) => item === requestedView) ?? "overview");
  const [visitedPreparation, setVisitedPreparation] = useState<string>();
  useEffect(() => {
    if (view === "preparation") setVisitedPreparation(id);
  }, [view, id]);
  const viewUrl = (next: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("view", next);
    return `?${params.toString()}`;
  };
  const anchorRoot = useProjectAnchor(hash, id);

  const project = useProject(id);
  const company = useCompany(project.data?.company_id);
  // 9.BF.1 — le nom du conseiller, pas son identifiant. Le hook a été corrigé
  // en 9.BB.1 (il lisait `resp.user` sur un wire plat).
  const courtier = useBroker(project.data?.broker_id ?? undefined);

  if (project.isLoading) {
    return (
      <div className="mx-auto w-full max-w-none">
        <div className="h-32 rounded-xl bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none" />
      </div>
    );
  }
  if (project.error) {
    return (
      <div className="mx-auto w-full max-w-none">
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
          {project.error instanceof ApiException
            ? getApiErrorMessage(project.error, lang) || t("errors.generic")
            : t("errors.generic")}
        </div>
      </div>
    );
  }
  const p = project.data;
  if (!p) return null;

  return (
    <div ref={anchorRoot} className="mx-auto w-full max-w-none">
      {/* Local titles stay in document flow; only the compact navigation remains pinned. */}
      <header className="relative z-10 -mx-4 border-b border-slate-200 bg-white px-4 py-4 lg:-mx-8 lg:px-8 lg:py-5">
        <nav aria-label="breadcrumb" className="mb-2 text-sm">
          <Link
            to="/projects"
            className="focus-visible:ring-brand-500 rounded text-slate-600 hover:text-slate-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            ← {t("projects.title")}
          </Link>
        </nav>
        <div className="flex flex-col items-start gap-3 xl:flex-row xl:justify-between">
          <div className="w-full min-w-0 xl:flex-1">
            <h1 className="break-words text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              {p.title}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              {p.status === "archived" ? (
                <ProjectStatusBadge status={p.status} />
              ) : view !== "preparation" ? (
                <EtatOperationnelBadge
                  lifecycleState={p.lifecycle_state}
                  etat={p.operational_state}
                  activityScope={p.activity_scope}
                  enRetard={p.is_late}
                />
              ) : null}
              {company.data && (
                <Link
                  className="text-brand-700 min-w-0 break-words hover:underline"
                  to={`/companies/${p.company_id}`}
                >
                  {company.data.legal_name}
                </Link>
              )}
              {courtier.data?.full_name && (
                <span className="text-sm text-slate-600">{courtier.data.full_name}</span>
              )}
              {p.reference && <span className="text-sm text-slate-600">{p.reference}</span>}
              {p.stage_label && <span className="text-sm text-slate-500">· {p.stage_label}</span>}
            </div>
          </div>
          <div className="flex w-full flex-wrap items-center gap-2 xl:w-auto xl:shrink-0">
            {/* 9.Q.1 — CTA comparaison soumissions (toujours visible ; la page
                affiche un empty/help state si < 2 soumissions extraites). */}
            <Link
              to={`/projects/${p.id}/comparison`}
              className="focus-visible:ring-brand-500 inline-flex min-h-11 touch-manipulation items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 motion-reduce:transition-none"
            >
              {t("comparison.cta")}
            </Link>
            <Link
              to={`/projects/${p.id}/edit`}
              className="min-h-11 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium"
            >
              {t("common.edit")}
            </Link>
          </div>
        </div>
      </header>

      <ProjectLifecycleSection key={p.id} projectId={p.id} />
      <nav
        data-project-anchor-occluder
        aria-label={t("workspace.views")}
        className="-mx-4 flex flex-wrap border-b border-slate-200 bg-white px-4 lg:-mx-8 lg:px-8"
      >
        {PROJECT_VIEWS.map((item) => (
          <Link
            key={item}
            to={viewUrl(item)}
            aria-current={view === item ? "page" : undefined}
            className={`focus-visible:ring-brand-500 inline-flex min-h-11 items-center px-3 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 ${view === item ? "border-brand-600 text-brand-800 border-b-2 bg-white" : "text-slate-600 hover:bg-slate-100"}`}
          >
            {t(`workspace.tabs.${item}`)}
          </Link>
        ))}
      </nav>
      <div className="space-y-5 py-6 lg:py-8">
        {/* 1 — Ce qu'il faut faire. Le verdict vient du serveur (9.AC.1) et il
            est ici seul : trois sections le répétaient auparavant. */}
        {view === "overview" && (
          <>
            <ProchaineAction projectId={p.id} />

            {/* Ce qui presse, immédiatement sous la prochaine action : une échéance
            n'a de sens qu'à côté de ce qu'elle retarde. */}
            <ProjectDeadlinesSection projectId={p.id} />
          </>
        )}

        {/* 2 — Les demandes. C'est l'objet central d'un dossier en cours :
            cahier de charges, assureurs, état de chacun, envoi. */}
        {/* Keep form state while changing views. */}
        <div hidden={view !== "preparation"} className="space-y-5">
          {(view === "preparation" || visitedPreparation === p.id) && (
            <RequestContext key={p.id} projectId={p.id}>
              {(requestId) => (
                <PrepareSendWorkspace key={requestId} id={requestId} presentation="dossier" />
              )}
            </RequestContext>
          )}
          <details open={hash === "#quote-requests" || undefined}>
            <summary className="min-h-11 cursor-pointer py-3 font-medium">
              {t("foundation.other_cycles")}
            </summary>
            <div id="quote-requests" tabIndex={-1}>
              <ProjectQuoteRequestsSection projectId={p.id} />
            </div>
          </details>
        </div>

        {/* Qui a répondu, qui se tait — la lecture par assureur des demandes
            ci-dessus, et non une seconde liste de demandes. */}
        {view === "insurers" && (
          <>
            <p className="text-sm text-slate-600">{t("workspace.project_scope")}</p>
            <InsurerLoopSection projectId={p.id} />
          </>
        )}

        {/* 3 — Les réponses, et le comparateur qui les met côte à côte. */}
        {view === "documents" && (
          <Link
            to={`/reponses?project_id=${p.id}`}
            className="focus-visible:ring-brand-500 text-brand-700 inline-flex min-h-11 items-center rounded-md px-2 text-sm font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            {t("project_hub.form_responses")}
          </Link>
        )}
        {view === "comparison" && (
          <>
            <p className="text-sm text-slate-600">{t("workspace.project_scope")}</p>
            <Link
              to={`/projects/${p.id}/comparison`}
              className="text-brand-700 inline-flex min-h-11 items-center rounded px-2 font-medium underline focus-visible:ring-2"
            >
              {t("comparison.cta")}
            </Link>
            <ProjectSubmissionsList projectId={p.id} />
          </>
        )}

        {/* 4 — Ce qui manque, sous UN seul titre. Deux objets distincts vivent
            là — les exigences du dossier (`project_requirements`) et les
            informations manquantes (`missing_information`) — mais le conseiller
            se pose une seule question, et il ne devrait pas avoir à savoir
            laquelle des deux listes porte sa réponse. */}
        <div hidden={view !== "preparation"}>
          <details open={hash === "#census" || undefined} className="space-y-5">
            <summary className="min-h-11 cursor-pointer py-3 font-medium">
              {t("foundation.additional_requirements")}
            </summary>
            <section aria-labelledby="ce-qui-manque" className="space-y-5">
              <h2 id="ce-qui-manque" className="sr-only">
                {t("project_hub.missing.title")}
              </h2>
              <DossierReadinessSection projectId={p.id} companyId={p.company_id ?? null} />
              <MissingInformationSection scope="project" projectId={p.id} />
              {/* 9.BK.1 — la liste d'employés est une condition d'envoi comme les
              autres : elle vit avec ce qui manque, pas dans un onglet à part. */}
              {hash === "#census" && (
                <div id="census" tabIndex={-1}>
                  <PreparationReturn projectId={p.id} />
                  <CensusSection key={p.id} projectId={p.id} companyId={p.company_id ?? null} />
                </div>
              )}
            </section>
          </details>
        </div>

        {/* 5 — Le dossier lui-même : ce qui ne bouge pas d'une visite à
            l'autre. Replié, parce que le traverser à chaque fois pour atteindre
            ce qui bouge était le coût principal de l'ancienne page. */}
        {view === "overview" && (
          <BlocRepliable titre={t("project_hub.file.title")} indice={p.reference ?? undefined}>
            <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
              <Field
                label={t("projects.fields.company")}
                value={company.data?.legal_name ?? null}
                link={p.company_id ? `/companies/${p.company_id}` : undefined}
              />
              {/* 9.BF.1 — le NOM, plus l'identifiant technique. Un UUID dans une
                interface n'apprend rien à personne. */}
              <Field label={t("projects.fields.broker")} value={courtier.data?.full_name ?? null} />
              <Field label={t("projects.fields.effect_date")} value={p.effect_date} />
              <Field
                label={t("projects.fields.renewal_target_date")}
                value={p.renewal_target_date}
              />
              <Field label={t("projects.fields.deadline_date")} value={p.deadline_date} />
              <Field
                label={t("projects.fields.progress")}
                value={p.progress !== null ? `${p.progress}%` : null}
              />
            </div>

            {p.notes && (
              <p className="mt-4 whitespace-pre-wrap text-sm text-slate-700">{p.notes}</p>
            )}
            {p.tags.length > 0 && (
              <div className="mt-4">
                <ChipList label={t("projects.sections.tags")} items={p.tags} />
              </div>
            )}
          </BlocRepliable>
        )}

        {/* Les fichiers et les documents liés : deux surfaces distinctes, mais
            une seule question — « qu'est-ce que ce dossier contient ». */}
        <div hidden={view !== "documents"}>
          <section aria-label={t("project_hub.documents.title")}>
            <div className="space-y-5">
              <ProjectFilesSection projectId={p.id} />
              <DocumentLinksSection scope="project" projectId={p.id} />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function Field({
  label,
  value,
  link,
  mono,
}: {
  label: string;
  value: string | number | null | undefined;
  link?: string;
  mono?: boolean;
}) {
  const display = value !== null && value !== undefined && value !== "" ? value : null;
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd
        className={
          mono ? "mt-0.5 font-mono text-xs text-slate-600" : "mt-0.5 text-sm text-slate-800"
        }
      >
        {display === null ? (
          <span className="text-slate-400">—</span>
        ) : link ? (
          <Link
            to={link}
            className="text-brand-700 focus-visible:ring-brand-500 rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            {display}
          </Link>
        ) : (
          display
        )}
      </dd>
    </div>
  );
}

function ChipList({ label, items }: { label: string; items: readonly string[] }) {
  return (
    <div className="sm:col-span-2">
      {label && <dt className="mb-1.5 text-xs text-slate-500">{label}</dt>}
      <dd>
        {items.length === 0 ? (
          <span className="text-sm text-slate-400">—</span>
        ) : (
          <ul className="flex flex-wrap gap-1.5">
            {items.map((it) => (
              <li
                key={it}
                className="inline-flex rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700"
              >
                {it}
              </li>
            ))}
          </ul>
        )}
      </dd>
    </div>
  );
}
